import type {
  AnimalUpdateInput,
  StudInfoUncheckedCreateWithoutAnimalInput,
  StudInfoUncheckedUpdateWithoutAnimalInput,
} from '@gql/graphql';
import { AnimalTypeEnum } from '@gql/graphql';
import type { MtAnimalsBreederPageFormValues } from '@presentation/pages';
import cleanDeep from 'clean-deep';
import { useCallback } from 'react';

export const useAnimalUpdateBuilder = () => {
  const animalUpdateBuilder = useCallback((values: Partial<MtAnimalsBreederPageFormValues>) => {
    const data: AnimalUpdateInput = {
      sensitivities: values.sensitivities,
      ...cleanDeep(
        {
          type: { set: AnimalTypeEnum.DOG },
          name: { set: values.name },
          birthday: { set: values.birthday?.toISOString() },
          birthdayType: { set: values.birthdayType },
          gender: { set: values.gender },
          size: { set: values.size ?? null },
          weight: { set: values.weight ?? null },
        },
        { nullValues: false },
      ),
    };

    data.dogInfos = {
      update: {
        data: {
          ...cleanDeep(
            {
              breederIsDogOwner: { set: values.dogInfo?.breederIsDogOwner },
              pedigree: { set: values.dogInfo?.pedigree },
              pedigreeNumber: { set: values.dogInfo?.pedigreeNumber },
              microshipNumber: { set: values.dogInfo?.microshipNumber },
              competitionTitles: { set: values.dogInfo?.competitionTitles },
              healthTestsResults: { set: values.dogInfo?.healthTestsResults },
              dogBreed: { connect: { id: values.dogInfo?.breedId } },
              color: { set: values.dogInfo?.color ?? null },
              coat: { set: values.dogInfo?.coat ?? null },
            },
            { nullValues: false },
          ),
        },
      },
    };

    data.studInfos = {
      upsert: {
        create: {
          ...cleanDeep({
            availableForBreeding: values.studInfo?.availableForBreeding,
            price: values.studInfo?.knownPrice ? values.studInfo?.price : null,
            accomodation: values.studInfo?.accomodation,
            type: values.studInfo?.type,
            champion: values.studInfo?.champion,
            adn: values.studInfo?.adn,
            description: values.studInfo?.description,
          } as StudInfoUncheckedCreateWithoutAnimalInput),
        },
        update: {
          ...cleanDeep(
            {
              availableForBreeding: { set: values.studInfo?.availableForBreeding },
              price: { set: values.studInfo?.knownPrice ? values.studInfo?.price : null },
              accomodation: { set: values.studInfo?.accomodation },
              type: { set: values.studInfo?.type },
              champion: { set: values.studInfo?.champion },
              adn: { set: values.studInfo?.adn },
              description: { set: values.studInfo?.description },
            } as StudInfoUncheckedUpdateWithoutAnimalInput,
            { nullValues: false },
          ),
        },
      },
    };

    return { data };
  }, []);

  return { animalUpdateBuilder };
};
